import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  User,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { firebaseAuth } from "./services/firebase";

type AuthChangeDetail = {
  isLoggedIn: boolean;
  username?: string;
  uid?: string;
  email?: string | null;
};

type AuthContextValue = {
  user: User | null;
  userId: string;
  userEmail: string;
  isLoggedIn: boolean;
  authLoading: boolean;
  authError: string;
  login: (email: string, password: string) => Promise<boolean>;
  register: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  clearAuthError: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SHARED_AUTH_KEY = "dear_language_demo_course_auth";
const SHARED_AUTH_USER_KEY = "dear_language_demo_user_name";
const SHARED_AUTH_EVENT = "dear-language-auth-change";

const getFriendlyFirebaseError = (error: unknown): string => {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: string }).code)
      : "";

  switch (code) {
    case "auth/email-already-in-use":
      return "Diese E-Mail-Adresse ist bereits registriert.";
    case "auth/invalid-email":
      return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "E-Mail oder Passwort ist nicht korrekt.";
    case "auth/weak-password":
      return "Das Passwort muss mindestens 6 Zeichen lang sein.";
    case "auth/network-request-failed":
      return "Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.";
    default:
      return "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.";
  }
};

const dispatchSharedAuthState = (user: User | null): void => {
  const detail: AuthChangeDetail = {
    isLoggedIn: Boolean(user),
    username: user?.displayName || user?.email || undefined,
    uid: user?.uid,
    email: user?.email,
  };

  try {
    if (user) {
      window.localStorage.setItem(SHARED_AUTH_KEY, "true");
      window.localStorage.setItem(
        SHARED_AUTH_USER_KEY,
        user.displayName || user.email || user.uid,
      );
    } else {
      window.localStorage.removeItem(SHARED_AUTH_KEY);
      window.localStorage.removeItem(SHARED_AUTH_USER_KEY);
    }
  } catch {
    // localStorage can be blocked.
  }

  window.dispatchEvent(
    new CustomEvent<AuthChangeDetail>(SHARED_AUTH_EVENT, {
      detail,
    }),
  );
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string>("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      dispatchSharedAuthState(firebaseUser);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setAuthError("");

    try {
      await signInWithEmailAndPassword(
        firebaseAuth,
        email.trim().toLowerCase(),
        password,
      );

      return true;
    } catch (error) {
      setAuthError(getFriendlyFirebaseError(error));
      return false;
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName?: string,
  ): Promise<boolean> => {
    setAuthError("");

    try {
      const credential = await createUserWithEmailAndPassword(
        firebaseAuth,
        email.trim().toLowerCase(),
        password,
      );

      if (displayName?.trim()) {
        await updateProfile(credential.user, {
          displayName: displayName.trim(),
        });
      }

      dispatchSharedAuthState(credential.user);
      return true;
    } catch (error) {
      setAuthError(getFriendlyFirebaseError(error));
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    setAuthError("");
    await signOut(firebaseAuth);
    dispatchSharedAuthState(null);
  };

  const clearAuthError = (): void => {
    setAuthError("");
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      userId: user?.uid ?? "",
      userEmail: user?.email ?? "",
      isLoggedIn: Boolean(user),
      authLoading,
      authError,
      login,
      register,
      logout,
      clearAuthError,
    }),
    [user, authLoading, authError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }

  return context;
};
